import { Alert, Button, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { SectionCard } from "../components/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { decodeFrame, normaliseSerial, scanningAvailable } from "./barcode";

/**
 * Camera barcode scanner for a battery serial number.
 *
 * Opens the rear camera, samples frames onto a canvas and decodes them. It
 * requires TWO matching reads before accepting a value: a single frame of a
 * barcode at an angle, in sunlight, through a scratched label is exactly the
 * situation that produces a plausible-but-wrong number, and a wrong serial on
 * a warranty claim is worse than no serial at all.
 *
 * Typing the number is always available and is never hidden behind this - the
 * scanner is an accelerator, not a gate. Some devices have no usable
 * decoder (iOS Safari without the wasm fallback loading), some users decline
 * camera permission, and some labels are simply unreadable.
 */
export function BarcodeScanner({
  onRead,
  onClose,
}: {
  onRead: (value: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  /** Last value seen, and how many frames in a row have agreed with it. */
  const agreeingRef = useRef<{ value: string; count: number }>({ value: "", count: 0 });
  const doneRef = useRef(false);

  const [state, setState] = useState<"starting" | "scanning" | "failed">("starting");
  const [message, setMessage] = useState<string>();
  const [hint, setHint] = useState<string>();

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    // Every track, explicitly. A stream left running keeps the camera light on
    // and the phone warm, and users reasonably read that as the app spying.
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!(await scanningAvailable())) {
        if (cancelled) return;
        setState("failed");
        setMessage(
          "This browser cannot read barcodes. Type the serial number instead — " +
            "it is exactly the same record either way.",
        );
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        if (cancelled) return;
        setState("failed");
        setMessage("This browser gives no camera access. Type the serial number instead.");
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            // The rear camera, and a resolution high enough to resolve the
            // thin bars of a Code 128 label without being so large that
            // decoding a frame takes longer than the next frame arrives.
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        if (cancelled) return;
        setState("scanning");
        setHint("Fill the frame with the barcode and hold steady.");
        tick();
      } catch (error) {
        if (cancelled) return;
        setState("failed");
        setMessage(
          error instanceof DOMException && error.name === "NotAllowedError"
            ? "Camera access was declined. Type the serial number instead, or allow " +
              "the camera in your browser settings and try again."
            : "The camera could not be opened. Type the serial number instead.",
        );
      }
    })();

    function tick() {
      frameRef.current = requestAnimationFrame(() => {
        void (async () => {
          if (cancelled || doneRef.current) return;
          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (!video || !canvas || video.readyState < 2) {
            tick();
            return;
          }

          const width = video.videoWidth;
          const height = video.videoHeight;
          if (width === 0 || height === 0) {
            tick();
            return;
          }
          if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
          }
          const context = canvas.getContext("2d", { willReadFrequently: true });
          if (!context) return;
          context.drawImage(video, 0, 0, width, height);

          const read = await decodeFrame(canvas);
          if (cancelled || doneRef.current) return;

          if (read) {
            const value = normaliseSerial(read.value);
            const agreeing = agreeingRef.current;
            agreeingRef.current =
              agreeing.value === value
                ? { value, count: agreeing.count + 1 }
                : { value, count: 1 };

            if (agreeingRef.current.count >= 2) {
              doneRef.current = true;
              stop();
              onRead(value);
              return;
            }
            setHint(`Reading ${value}… hold steady`);
          }
          tick();
        })();
      });
    }

    return () => {
      cancelled = true;
      stop();
    };
    // onRead/stop are stable for the lifetime of this sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SectionCard
      title="Scan the serial number"
      action={
        <Button variant="default" size="sm" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <Stack gap="sm">
        {state === "scanning" && hint && (
          <Text fz="md" c="gray.7">
            {hint}
          </Text>
        )}

        {state === "failed" ? (
          <Alert
            variant="light"
            color="yellow"
            radius="md"
            icon={<IconAlertTriangle size={18} stroke={1.8} />}
          >
            {message}
          </Alert>
        ) : (
          <div className="scanner">
            <video
              ref={videoRef}
              className="scanner__video"
              playsInline
              muted
              aria-label="Camera view"
            />
            <div className="scanner__reticle" aria-hidden="true" />
          </div>
        )}
        {state === "starting" && (
          <Text fz="md" c="gray.6">
            Opening the camera…
          </Text>
        )}

        {/* Never rendered, never shown: the frame buffer decoding reads from. */}
        <canvas ref={canvasRef} className="visually-hidden" />

        {/* The wording matters and is asserted by the scanner drive: opening
            the camera must never be a dead end on a device without one. */}
        <Button variant="default" size="md" fullWidth onClick={onClose}>
          Type it instead
        </Button>
      </Stack>
    </SectionCard>
  );
}
